/**
 * 한 시도(attempt)의 계측 (TASK-2026-002 AC-02·03·04).
 *
 * 시도의 수명과 집계를 화면에서 떼어 이 모듈에 모은다. 이유는 두 가지다.
 *
 * 1. **단일 책임.** 이전 구현은 시작을 `useEffect`가, 종료를 `move()`와
 *    `restart()`가 각각 남겼다. 그래서 재시작에는 시작이 빠지고, 클리어한
 *    시도를 다시 시작하면 이미 남긴 완주 경로가 한 번 더 쌓였다. 여기서는
 *    `begin`과 `finish`가 유일한 통로이고, 끝난 시도는 두 번 끝나지 않는다.
 * 2. **DOM 없이 검사할 수 있다.** 이 저장소의 vitest 환경은 `node`이고
 *    React 렌더링 테스트 도구는 승인된 의존성이 아니다. 계측 규칙을 순수
 *    함수로 두면 화면을 띄우지 않고도 규칙 자체를 검사할 수 있다.
 *
 * 이 모듈은 이벤트를 **기록하지 않는다.** 무엇을 남길지 계산해 돌려주기만
 * 하고, 실제 기록은 호출자가 한다. 전송 경로가 여기 들어올 자리가 없다(AC-25).
 */

import type { TelemetryEventName, TelemetryPayload } from '../telemetry/events';

/** 호출자가 그대로 `telemetry().record(...)`에 넘길 수 있는 형태. */
export interface PendingEvent {
  readonly name: TelemetryEventName;
  readonly payload: TelemetryPayload;
}

/**
 * 시도가 어떻게 끝났는가 (중앙 온라인 명세 F1-05의 `end reason`).
 *
 * 값은 중앙 명세가 고정한 `clear|restart|tier_change|quit|interrupted` 다섯
 * 개뿐이다. 늘리려면 CR과 G1 재승인이 필요하므로 여기서 임의로 더하지 않는다.
 *
 * 리플레이를 보려고 판을 떠난 것은 `quit`으로 남긴다. 전용 값을 만들면 승인된
 * enum을 벗어나기 때문이다. 관찰이었다는 사실은 바로 앞에 남는 `replay_view`
 * 이벤트로 이어 읽는다.
 */
export type AttemptEndReason =
  /** 목표를 모두 열었다. */
  | 'clear'
  /** 사용자가 다시 시작했다. */
  | 'restart'
  /** 단계를 옮기며 시도를 버렸다. */
  | 'tier_change'
  /** 화면을 떠났다. 리플레이 관찰 진입도 여기에 든다. */
  | 'quit'
  /**
   * 브라우저를 닫거나 새로고침해 끝이 기록되지 못했다.
   *
   * 다음 실행이 남긴 checkpoint를 보고 뒤늦게 닫는 종료다. 살아 있는 화면이
   * 낸 종료와 섞으면 "중간에 그만둔 사람"의 수를 셀 수 없다.
   */
  | 'interrupted';

/** 승인된 종료 사유 전부. 검사와 문서가 같은 목록을 본다. */
export const ATTEMPT_END_REASONS: readonly AttemptEndReason[] = Object.freeze([
  'clear',
  'restart',
  'tier_change',
  'quit',
  'interrupted',
]);

export interface AttemptCounters {
  /** 실제로 큐브가 움직인 수. */
  readonly moves: number;
  /** 보드 밖이라 거부된 입력 수. 이동으로 세지 않는다. */
  readonly rejectedInputs: number;
  /** 플레이어가 직접 연 목표 타일 수. 마무리 오픈은 포함하지 않는다. */
  readonly openedTargets: number;
  /** 플레이어가 직접 연 비목표(장식) 타일 수. */
  readonly openedDecorations: number;
  /** 첫 수까지 걸린 시간(ms). 끝내 두지 않았으면 없다. */
  readonly firstMoveMs: number | undefined;
  /**
   * 첫 타일을 연 시각(ms). **목표든 장식이든 상관없다.**
   *
   * `DEC-044`가 정한 원래 의미(진행자 기록양식의 `firstOpenSec`)를 그대로
   * 둔다. 한때 이 값을 첫 목표 전용으로 바꿨다가 독립 리뷰(REV-003)에서
   * 되돌렸다 — 필드 이름은 그대로 두고 의미만 바꾸면 과거 기록과 새 기록이
   * 같은 이름으로 다른 것을 뜻하게 되고, 그 차이는 데이터만 봐서는 보이지 않는다.
   */
  readonly firstOpenMs: number | undefined;
  /** 첫 **목표**를 연 시각(ms). 장식은 이 값을 움직이지 않는다(AC-03). */
  readonly firstTargetOpenMs: number | undefined;
  /**
   * 상한을 넘겨 경로 기록이 잘렸는가 (중앙 명세 「시도 정확성」).
   *
   * 2,000수를 넘어도 게임을 멈추지 않는다. 다만 그 뒤의 수는 경로에 담기지
   * 않으므로 **측정이 불완전하다는 사실을 숨기지 않는다.** 예전에는 상한을
   * 넘으면 스키마 검사에 걸려 기록 자체가 조용히 사라졌다.
   */
  readonly truncated: boolean;
  /** 이번 시도의 경로. 한 수당 한 글자. 상한까지만 담는다. */
  readonly path: string;
  /** 각 수의 직전 수로부터의 간격(ms). */
  readonly deltasMs: readonly number[];
}

export interface AttemptState {
  readonly stageId: string;
  readonly tier: number;
  /**
   * 이 시도의 고유 ID (중앙 명세 F1-05).
   *
   * 순번(`attempt`)만으로는 모자란다. 순번은 스테이지 안에서만 뜻이 있고,
   * 여러 세션·여러 기기의 기록이 한자리에 모이면 같은 값이 수없이 겹친다.
   * 종료·경로 조각이 어느 시작에 붙는지는 이 ID가 정한다.
   *
   * 참가자 식별자가 **아니다.** 시도마다 새로 만들고 어디에도 재사용하지
   * 않으며, 기기나 사람을 가리키는 값을 재료로 쓰지 않는다.
   */
  readonly attemptId: string;
  /** 같은 스테이지의 직전 시도 ID. 첫 시도면 없다. */
  readonly previousAttemptId: string | undefined;
  /** 스테이지 안에서 1부터 증가하는 시도 번호(ordinal). */
  readonly attempt: number;
  readonly startedAt: number;
  /** 마지막 수의 시각. 다음 수의 간격을 재는 기준. */
  readonly lastMoveAt: number;
  /** 이미 끝난 시도인가. 끝난 시도는 다시 끝나지 않는다. */
  readonly finished: boolean;
  /**
   * 멈춰 있는 동안 흘린 시간의 합(ms)과 멈춘 시각.
   *
   * 도움말을 읽는 동안에도 시계는 흐른다. 그것을 플레이 시간에 넣으면
   * "규칙을 오래 읽은 사람"과 "판을 오래 붙든 사람"이 같은 숫자가 된다.
   */
  readonly pausedMs: number;
  readonly pausedAt: number | undefined;
  /** 결과 조작 가능 시점을 이미 남겼는가. */
  readonly resultReadyRecorded: boolean;
  readonly counters: AttemptCounters;
}

const EMPTY_COUNTERS: AttemptCounters = Object.freeze({
  moves: 0,
  rejectedInputs: 0,
  openedTargets: 0,
  openedDecorations: 0,
  firstMoveMs: undefined,
  firstOpenMs: undefined,
  firstTargetOpenMs: undefined,
  truncated: false,
  path: '',
  deltasMs: Object.freeze([]),
});

/** 한 수의 간격 상한. 스키마의 `deltas_ms` 제약과 같은 값이다. */
const MAX_DELTA_MS = 600_000;

/** 경로에 담는 최대 수. 중앙 명세가 고정한 상한이다. */
export const MAX_PATH_MOVES = 2000;

export interface BeginOptions {
  readonly stageId: string;
  readonly tier: number;
  readonly at: number;
  /** 스테이지 안의 순번. 스테이지보다 오래 사는 쪽이 세어 넘겨준다. */
  readonly attempt: number;
  /** 이 시도의 고유 ID. 만드는 일은 호출자가 한다(순수 모듈은 난수를 만들지 않는다). */
  readonly attemptId: string;
  /** 같은 스테이지의 직전 시도 ID. 첫 시도면 없다. */
  readonly previousAttemptId?: string | undefined;
}

/**
 * 새 시도를 연다. 시작 이벤트는 여기서만 나온다.
 *
 * 순번과 ID를 **받는다**. 예전에는 순번을 직전 시도 상태에서 유도했는데,
 * 화면이 단계를 옮길 때 플레이 영역째 다시 마운트되면서 그 상태가 사라져
 * 번호가 1로 되돌아갔다(REV-002). ID는 난수를 쓰므로 순수 모듈이 만들지
 * 않는다 — 만들면 이 모듈을 결정적으로 검사할 수 없다.
 */
export function begin(
  options: BeginOptions,
): { readonly state: AttemptState; readonly events: readonly PendingEvent[] } {
  const { stageId, tier, at, attempt, attemptId, previousAttemptId } = options;

  const state: AttemptState = {
    stageId,
    tier,
    attemptId,
    previousAttemptId,
    attempt,
    startedAt: at,
    lastMoveAt: at,
    finished: false,
    resultReadyRecorded: false,
    pausedMs: 0,
    pausedAt: undefined,
    counters: EMPTY_COUNTERS,
  };

  const events: PendingEvent[] = [
    {
      name: 'stage_start',
      payload: {
        stage_id: stageId,
        tier,
        attempt,
        attempt_id: attemptId,
        ...(previousAttemptId !== undefined ? { previous_attempt_id: previousAttemptId } : {}),
      },
    },
  ];

  /*
   * 튜토리얼 시작은 Tier 1의 **첫 시도**에만 붙인다. 다시 시작할 때마다
   * 붙이면 "튜토리얼을 몇 번 시작했는가"가 재시작 횟수와 같아져 버린다.
   */
  if (tier === 1 && attempt === 1) {
    events.push({ name: 'tutorial_start', payload: { stage_id: stageId, tier } });
  }

  return { state, events };
}

/** 보드 밖이라 거부된 입력. 이동 수와 경로를 건드리지 않는다. */
export function rejectInput(state: AttemptState): AttemptState {
  return {
    ...state,
    counters: { ...state.counters, rejectedInputs: state.counters.rejectedInputs + 1 },
  };
}

export interface MoveOutcome {
  /** 한 글자 방향(`n`/`s`/`e`/`w`). */
  readonly letter: string;
  /** 이 수로 타일이 열렸는가. 열렸다면 그것이 목표였는가. */
  readonly opened: 'target' | 'decoration' | 'none';
}

/**
 * 실제로 움직인 수 하나를 반영한다.
 *
 * `firstOpenMs`는 **목표를 연 순간에만** 잡는다. 이전 구현은 장식을 열어도
 * 잡아서, "첫 조각을 여는 데 얼마나 걸렸는가"가 목표를 찾은 시간이 아니라
 * 아무 칸이나 밟은 시간이 되어 있었다(AC-03).
 */
export function applyMove(state: AttemptState, outcome: MoveOutcome, at: number): AttemptState {
  const counters = state.counters;
  const delta = Math.min(MAX_DELTA_MS, Math.max(0, Math.round(at - state.lastMoveAt)));

  /*
   * 경로는 상한까지만 담는다. 넘어도 게임을 멈추지 않고 이동 수는 계속 센다
   * (중앙 명세 「시도 정확성」). 예전에는 그냥 이어 붙여서, 상한을 넘긴 순간
   * 스키마 검사에 걸려 **기록 전체가 조용히 사라졌다.**
   */
  const full = counters.path.length >= MAX_PATH_MOVES;

  return {
    ...state,
    lastMoveAt: at,
    counters: {
      ...counters,
      moves: counters.moves + 1,
      truncated: counters.truncated || full,
      path: full ? counters.path : counters.path + outcome.letter,
      deltasMs: full ? counters.deltasMs : [...counters.deltasMs, delta],
      firstMoveMs: counters.firstMoveMs ?? elapsed(state, at),
      openedTargets: counters.openedTargets + (outcome.opened === 'target' ? 1 : 0),
      openedDecorations: counters.openedDecorations + (outcome.opened === 'decoration' ? 1 : 0),
      firstOpenMs:
        outcome.opened === 'none' ? counters.firstOpenMs : (counters.firstOpenMs ?? elapsed(state, at)),
      firstTargetOpenMs:
        outcome.opened === 'target'
          ? (counters.firstTargetOpenMs ?? elapsed(state, at))
          : counters.firstTargetOpenMs,
    },
  };
}

/**
 * 시도가 시작된 뒤 **실제로 플레이한** 시간(ms).
 *
 * 멈춰 있던 시간은 빼고 센다. 도움말을 읽는 동안 흐른 시계가 플레이 시간에
 * 들어가면 첫 수까지의 시간이 규칙을 읽은 시간과 섞인다.
 */
function elapsed(state: AttemptState, at: number): number {
  const pausedNow = state.pausedAt === undefined ? 0 : at - state.pausedAt;
  return Math.max(0, Math.round(at - state.startedAt - state.pausedMs - pausedNow));
}

/**
 * 시도를 멈춘다. 도움말처럼 판을 떠나 있는 동안 시계를 세운다.
 *
 * 이미 멈춰 있으면 아무 일도 하지 않는다 — 두 번 멈추면 재개할 때 빼는
 * 시간이 어긋난다.
 */
export function pause(state: AttemptState, at: number): AttemptState {
  if (state.pausedAt !== undefined || state.finished) {
    return state;
  }
  return { ...state, pausedAt: at };
}

/** 멈춘 만큼을 누적하고 다시 센다. 마지막 수의 기준 시각도 함께 민다. */
export function resume(state: AttemptState, at: number): AttemptState {
  if (state.pausedAt === undefined) {
    return state;
  }
  const away = Math.max(0, at - state.pausedAt);
  return {
    ...state,
    pausedAt: undefined,
    pausedMs: state.pausedMs + away,
    // 멈춰 있던 시간이 다음 수의 간격으로 잡히면 재생이 그만큼 멈춰 선다.
    lastMoveAt: state.lastMoveAt + away,
  };
}

/** 값이 있을 때만 키를 넣는다. 0으로 채우면 "즉시 했다"와 구분되지 않는다. */
function latency(counters: AttemptCounters): TelemetryPayload {
  return {
    ...(counters.firstMoveMs !== undefined ? { first_move_ms: counters.firstMoveMs } : {}),
    ...(counters.firstOpenMs !== undefined ? { first_open_ms: counters.firstOpenMs } : {}),
    ...(counters.firstTargetOpenMs !== undefined
      ? { first_target_open_ms: counters.firstTargetOpenMs }
      : {}),
  };
}

/**
 * 시도를 끝낸다. 종료 이벤트와 경로 기록은 여기서만 나온다.
 *
 * 이미 끝난 시도를 다시 끝내면 **아무 이벤트도 나오지 않는다.** 클리어한 뒤
 * `다시하기`를 누르면 이 경로를 지나는데, 이전 구현은 그때 완주 경로를 한 벌
 * 더 남겼다(AC-02).
 *
 * `at`은 **지금**이다. 실제로 시도가 끝난 시각은 사유에 따라 다르다 —
 * `interrupted`는 앱이 닫혀 있던 동안 끝난 것이므로, 마지막 활동까지만 플레이
 * 시간으로 세고 그 뒤의 공백은 `inactive_ms`로 따로 남긴다. 예전에는 공백을
 * 그대로 플레이 시간에 합산해, 한 시간 뒤 복구하면 `duration_ms`가 360만이
 * 되었다(REV-008).
 */
export function finish(
  state: AttemptState,
  reason: AttemptEndReason,
  at: number,
): { readonly state: AttemptState; readonly events: readonly PendingEvent[] } {
  if (state.finished) {
    return { state, events: [] };
  }

  const { stageId, tier, attempt, attemptId, previousAttemptId, counters } = state;

  /*
   * 중단은 마지막으로 무언가를 한 시점에 끝난 것으로 본다. 한 수도 두지
   * 않았다면 시작 시각이 그 시점이다.
   */
  const endedAt = reason === 'interrupted' ? Math.max(state.startedAt, state.lastMoveAt) : at;
  const inactiveMs = reason === 'interrupted' ? Math.max(0, Math.round(at - endedAt)) : 0;

  const base = {
    stage_id: stageId,
    tier,
    attempt,
    attempt_id: attemptId,
    ...(previousAttemptId !== undefined ? { previous_attempt_id: previousAttemptId } : {}),
    moves: counters.moves,
    duration_ms: elapsed(state, endedAt),
    paused_ms:
      state.pausedMs + (state.pausedAt === undefined ? 0 : Math.max(0, endedAt - state.pausedAt)),
    end_reason: reason,
    rejected_inputs: counters.rejectedInputs,
    ...latency(counters),
  };

  const events: PendingEvent[] = [];

  if (reason === 'clear') {
    events.push({
      name: 'stage_end',
      payload: {
        ...base,
        cleared: true,
        opened_targets: counters.openedTargets,
        opened_decorations: counters.openedDecorations,
      },
    });
  } else {
    /*
     * 끝까지 가지 못한 시도. `stage_retry`가 그 종료다.
     *
     * `abandoned`·`interrupted`는 `end_reason`에서 파생한 값이며, 판정의
     * 기준은 `end_reason`이다. 둘을 남겨 두는 것은 이 필드들을 이미 읽고
     * 있는 검사와 문서를 깨지 않기 위해서다.
     */
    events.push({
      name: 'stage_retry',
      payload: {
        ...base,
        abandoned: reason !== 'restart',
        interrupted: reason === 'interrupted',
        ...(inactiveMs > 0 ? { inactive_ms: inactiveMs } : {}),
        opened_targets: counters.openedTargets,
        opened_decorations: counters.openedDecorations,
      },
    });
  }

  /*
   * 경로는 움직인 수가 있을 때만 남긴다. 빈 경로는 재생할 것이 없고,
   * 재시작을 연달아 눌렀을 때 빈 기록이 목록을 채운다.
   */
  if (counters.path.length > 0) {
    events.push({
      name: 'stage_replay',
      payload: {
        stage_id: stageId,
        tier,
        attempt,
        attempt_id: attemptId,
        path: counters.path,
        deltas_ms: [...counters.deltasMs],
        // 상한을 넘겨 잘렸으면 숨기지 않는다.
        ...(counters.truncated ? { truncated: true } : {}),
      },
    });
  }

  if (reason === 'clear' && tier === 1) {
    events.push({
      name: 'tutorial_complete',
      payload: { stage_id: stageId, tier, moves: counters.moves, duration_ms: base.duration_ms },
    });
  }

  return { state: { ...state, finished: true }, events };
}

/**
 * 이 시도의 종료가 이미 로그에 있는가 (REV-030).
 *
 * 종료는 `stage_end`(클리어)와 `stage_retry`(그 밖의 사유)뿐이다. 경로나 결과
 * 준비 이벤트도 같은 ID를 달지만 종료가 아니다. checkpoint 정리를 확인하지
 * 못했을 때 같은 시도를 다시 닫지 않기 위한 마지막 확인이다.
 */
export function hasEndedAttempt(
  events: readonly { readonly name: string; readonly payload: Readonly<Record<string, unknown>> }[],
  attemptId: string,
): boolean {
  return events.some(
    (event) =>
      (event.name === 'stage_end' || event.name === 'stage_retry') &&
      event.payload['attempt_id'] === attemptId,
  );
}

/**
 * 결과 카드를 실제로 조작할 수 있게 된 시점 (AC-04).
 *
 * 클리어 순간이 아니라 개봉 연출이 끝나 버튼을 누를 수 있게 된 때다. 두 시각의
 * 차이가 참여자가 아무것도 할 수 없이 기다린 시간이며, 재도전까지의 공백을
 * 사후에 해석하려면 이 값이 있어야 한다. 한 시도에 한 번만 남는다.
 */
export function markResultReady(
  state: AttemptState,
  at: number,
  clearedAt: number,
): { readonly state: AttemptState; readonly events: readonly PendingEvent[] } {
  if (state.resultReadyRecorded) {
    return { state, events: [] };
  }

  return {
    state: { ...state, resultReadyRecorded: true },
    events: [
      {
        name: 'result_ready',
        payload: {
          stage_id: state.stageId,
          tier: state.tier,
          attempt: state.attempt,
          attempt_id: state.attemptId,
          delay_ms: Math.max(0, Math.round(at - clearedAt)),
        },
      },
    ],
  };
}

/* ------------------------------------------------------------------ *
 * 로컬 checkpoint와 경로 chunk (SPEC 「포함 범위」 4·5)
 * ------------------------------------------------------------------ */

/** checkpoint 저장 형식의 버전. 형식이 바뀌면 올리고, 다른 값은 버린다. */
export const CHECKPOINT_VERSION = 1;

export interface AttemptCheckpoint {
  readonly version: number;
  readonly state: AttemptState;
}

/**
 * 진행 중인 시도를 저장 가능한 형태로 바꾼다.
 *
 * 끝난 시도는 저장하지 않는다 — 복구할 것이 없고, 남겨 두면 다음 실행이
 * 이미 닫힌 시도를 한 번 더 닫는다.
 */
export function toCheckpoint(state: AttemptState): AttemptCheckpoint | undefined {
  if (state.finished) {
    return undefined;
  }
  return { version: CHECKPOINT_VERSION, state };
}

/**
 * 저장된 checkpoint를 되읽는다. 조건을 벗어나면 `undefined`.
 *
 * 저장소 값을 신뢰하지 않는다. 사용자가 직접 고칠 수 있고 다른 버전이 남긴
 * 것일 수도 있다. 모양이 맞지 않으면 조용히 버린다 — 깨진 checkpoint 때문에
 * 앱이 열리지 않는 편이 훨씬 나쁘다.
 */
export function fromCheckpoint(raw: unknown): AttemptState | undefined {
  if (typeof raw !== 'object' || raw === null) {
    return undefined;
  }
  const candidate = raw as { version?: unknown; state?: unknown };
  if (candidate.version !== CHECKPOINT_VERSION) {
    return undefined;
  }

  const state = candidate.state as Partial<AttemptState> | undefined;
  if (
    state === undefined ||
    typeof state.stageId !== 'string' ||
    typeof state.tier !== 'number' ||
    // ID가 없으면 복구해도 어느 시작의 종료인지 이을 수 없다.
    typeof state.attemptId !== 'string' ||
    state.attemptId.length === 0 ||
    typeof state.attempt !== 'number' ||
    typeof state.startedAt !== 'number' ||
    typeof state.finished !== 'boolean' ||
    typeof state.counters !== 'object' ||
    state.counters === null
  ) {
    return undefined;
  }

  const counters = state.counters as Partial<AttemptCounters>;
  if (typeof counters.path !== 'string' || !Array.isArray(counters.deltasMs)) {
    return undefined;
  }
  if (counters.path.length !== counters.deltasMs.length) {
    return undefined;
  }

  return {
    stageId: state.stageId,
    tier: state.tier,
    attemptId: state.attemptId,
    previousAttemptId:
      typeof state.previousAttemptId === 'string' ? state.previousAttemptId : undefined,
    attempt: state.attempt,
    startedAt: state.startedAt,
    lastMoveAt: typeof state.lastMoveAt === 'number' ? state.lastMoveAt : state.startedAt,
    finished: state.finished,
    resultReadyRecorded: state.resultReadyRecorded === true,
    pausedMs: typeof state.pausedMs === 'number' ? state.pausedMs : 0,
    pausedAt: undefined,
    counters: {
      moves: typeof counters.moves === 'number' ? counters.moves : 0,
      rejectedInputs: typeof counters.rejectedInputs === 'number' ? counters.rejectedInputs : 0,
      openedTargets: typeof counters.openedTargets === 'number' ? counters.openedTargets : 0,
      openedDecorations:
        typeof counters.openedDecorations === 'number' ? counters.openedDecorations : 0,
      firstMoveMs: typeof counters.firstMoveMs === 'number' ? counters.firstMoveMs : undefined,
      firstOpenMs: typeof counters.firstOpenMs === 'number' ? counters.firstOpenMs : undefined,
      firstTargetOpenMs:
        typeof counters.firstTargetOpenMs === 'number' ? counters.firstTargetOpenMs : undefined,
      truncated: counters.truncated === true,
      path: counters.path,
      deltasMs: counters.deltasMs.filter((item): item is number => typeof item === 'number'),
    },
  };
}

export interface PathChunk {
  /** 어느 시도의 조각인가. 중앙 명세는 `(attempt_id, chunk_index)`로 저장한다. */
  readonly attemptId: string;
  /** 0부터 세는 조각 번호. 받는 쪽이 순서를 복원하는 근거다. */
  readonly index: number;
  readonly path: string;
  readonly deltasMs: readonly number[];
  /**
   * 이 시도의 경로가 상한을 넘겨 잘렸는가.
   *
   * 모든 조각에 같은 값을 붙인다. 조각 하나만 받아 본 쪽도 "이 기록은
   * 완전하지 않다"를 알아야 하기 때문이다.
   */
  readonly truncated: boolean;
}

/**
 * 한 조각에 담는 최대 수. 중앙 명세가 고정한 상한이다(`경로 한 조각 ≤50수`).
 *
 * 한때 64로 두었다가 리뷰(REV-010)에서 되돌렸다. 승인된 상한을 코드가 기본값
 * 으로 어기고 있으면, 그 값을 읽고 만든 서버가 나중에 거절한다.
 */
export const MAX_CHUNK_MOVES = 50;
export const DEFAULT_CHUNK_MOVES = MAX_CHUNK_MOVES;

/**
 * 경로를 전송 단위로 쪼갠다 (SPEC 「포함 범위」 5, 중앙 명세 F1-05).
 *
 * **전송하지 않는다.** 여기에는 `fetch`도 주소도 없다. 다음 작업이 서버
 * 계약을 만들 때 쓸 순수 데이터 모델일 뿐이다. 경로와 간격을 같은 경계에서
 * 쪼개는 이유는 둘의 길이가 어긋나면 재생이 성립하지 않기 때문이다.
 */
export function toPathChunks(
  attemptId: string,
  path: string,
  deltasMs: readonly number[],
  options: { readonly size?: number; readonly truncated?: boolean } = {},
): readonly PathChunk[] {
  const size = options.size ?? DEFAULT_CHUNK_MOVES;
  const truncated = options.truncated ?? false;

  if (!Number.isInteger(size) || size < 1 || size > MAX_CHUNK_MOVES) {
    throw new Error(`조각 크기는 1 이상 ${String(MAX_CHUNK_MOVES)} 이하의 정수여야 합니다.`);
  }
  if (attemptId.length === 0) {
    throw new Error('조각은 어느 시도의 것인지 밝혀야 합니다.');
  }
  if (path.length !== deltasMs.length) {
    throw new Error('경로와 간격의 길이가 다릅니다.');
  }
  if (path.length > MAX_PATH_MOVES) {
    throw new Error(`경로가 상한(${String(MAX_PATH_MOVES)}수)을 넘습니다.`);
  }
  if (path.length === 0) {
    return [];
  }

  const chunks: PathChunk[] = [];
  for (let start = 0; start < path.length; start += size) {
    chunks.push({
      attemptId,
      index: chunks.length,
      path: path.slice(start, start + size),
      deltasMs: deltasMs.slice(start, start + size),
      truncated,
    });
  }
  return chunks;
}

/** 시도 상태에서 바로 조각을 만든다. 잘림 여부를 잊지 않고 함께 넘긴다. */
export function chunksOf(
  state: AttemptState,
  size: number = DEFAULT_CHUNK_MOVES,
): readonly PathChunk[] {
  return toPathChunks(state.attemptId, state.counters.path, state.counters.deltasMs, {
    size,
    truncated: state.counters.truncated,
  });
}
